/**
 * TimezoneSelect — Select com lista enxuta de IANA timezones + fallback custom.
 *
 * Usado em Perfil do usuario, edicao de tenant (admin) e wizard ZTNA.
 *
 * Quando user escolhe "Outro (IANA)..." o componente troca pra um TextInput
 * livre, validando minimamente que tem o formato `Region/City`.
 */
import { useState } from 'react'
import { Group, Select, Stack, Text, TextInput } from '@mantine/core'

export const COMMON_TIMEZONES: { value: string; label: string }[] = [
  { value: 'America/Sao_Paulo', label: 'Brasil — São Paulo (UTC-3)' },
  { value: 'America/Manaus', label: 'Brasil — Manaus (UTC-4)' },
  { value: 'America/Belem', label: 'Brasil — Belém (UTC-3)' },
  { value: 'America/Fortaleza', label: 'Brasil — Fortaleza (UTC-3)' },
  { value: 'America/Recife', label: 'Brasil — Recife (UTC-3)' },
  { value: 'America/Cuiaba', label: 'Brasil — Cuiabá (UTC-4)' },
  { value: 'America/Boa_Vista', label: 'Brasil — Boa Vista (UTC-4)' },
  { value: 'America/Porto_Velho', label: 'Brasil — Porto Velho (UTC-4)' },
  { value: 'America/Rio_Branco', label: 'Brasil — Rio Branco (UTC-5)' },
  { value: 'America/Noronha', label: 'Brasil — Fernando de Noronha (UTC-2)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/Lisbon', label: 'Portugal — Lisboa' },
  { value: 'America/New_York', label: 'EUA — Nova York (Eastern)' },
  { value: 'America/Los_Angeles', label: 'EUA — Los Angeles (Pacific)' },
]

const CUSTOM_SENTINEL = '__custom__'

interface Props {
  value: string | null | undefined
  onChange: (value: string | null) => void
  label?: string
  description?: string
  placeholder?: string
  /** Quando true, exibe opcao "Usar default do tenant/sistema" como null. */
  allowEmpty?: boolean
  /** Texto do "limpar/sem override". */
  emptyLabel?: string
  disabled?: boolean
}

export function TimezoneSelect({
  value,
  onChange,
  label = 'Fuso horário',
  description,
  placeholder = 'Selecione um fuso',
  allowEmpty = true,
  emptyLabel = 'Usar padrão do sistema',
  disabled = false,
}: Props) {
  const isInList =
    value != null && COMMON_TIMEZONES.some((tz) => tz.value === value)
  const isCustom = value != null && value !== '' && !isInList
  const [customMode, setCustomMode] = useState(isCustom)

  const data: { value: string; label: string }[] = []
  if (allowEmpty) data.push({ value: '', label: emptyLabel })
  data.push(...COMMON_TIMEZONES)
  data.push({ value: CUSTOM_SENTINEL, label: 'Outro (IANA)...' })

  const selectValue = customMode
    ? CUSTOM_SENTINEL
    : value == null
      ? ''
      : isInList
        ? value
        : ''

  return (
    <Stack gap={4}>
      <Select
        label={label}
        description={description}
        placeholder={placeholder}
        data={data}
        value={selectValue}
        searchable
        disabled={disabled}
        onChange={(v) => {
          if (v === CUSTOM_SENTINEL) {
            setCustomMode(true)
            return
          }
          setCustomMode(false)
          onChange(v === '' ? null : v)
        }}
      />
      {customMode ? (
        <Group gap="xs" align="flex-end" wrap="nowrap">
          <TextInput
            placeholder="ex: Asia/Tokyo, Europe/Berlin"
            value={value ?? ''}
            onChange={(e) => onChange(e.currentTarget.value || null)}
            description="Nome IANA exato (Continente/Cidade). Validação final no backend."
            style={{ flex: 1 }}
            disabled={disabled}
          />
        </Group>
      ) : null}
      {!customMode && value && !isInList ? (
        <Text size="xs" c="dimmed">
          Custom: <code>{value}</code>
        </Text>
      ) : null}
    </Stack>
  )
}

export default TimezoneSelect
